/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import { useI18n } from "hooks/i18n/i18n";
import { withNoTranslate } from "i18n/noTranslate";

import type { FC } from "react";

type Props = {
  maxApplicableQuantity: number;
  selectedQuantity: number | null;
  ticketName: string;
  ticketPriceAmount: number;
  onChange: (quantity: number | null) => void;
};

export const ResaleApplicationQuantitySelection: FC<Props> = ({
  maxApplicableQuantity,
  selectedQuantity,
  ticketName,
  ticketPriceAmount,
  onChange,
}) => {
  const { t } = useI18n();
  const classes = styles();
  const quantities = Array.from(
    { length: maxApplicableQuantity },
    (_, index) => index + 1
  );

  return (
    <section css={classes.root}>
      <div css={classes.ticket}>
        <p css={classes.ticketName} {...withNoTranslate()}>
          {ticketName}
        </p>
        <p css={classes.ticketPrice}>
          <span
            {...withNoTranslate()}
          >{`¥${ticketPriceAmount.toLocaleString()}`}</span>
          <span>（{t("event.resale.entry.faceValue")}）</span>
        </p>
      </div>
      <div css={classes.selectWrapper}>
        <select
          css={classes.select}
          aria-label={t("event.resale.entry.quantitySelectionLabel", {
            ticketName,
          })}
          value={selectedQuantity ?? 0}
          onChange={(event) => {
            const quantity = Number(event.target.value);

            onChange(quantity === 0 ? null : quantity);
          }}
        >
          <option value={0}>0</option>
          {quantities.map((quantity) => (
            <option key={quantity} value={quantity}>
              {quantity}
            </option>
          ))}
        </select>
        <span css={classes.selectIcon} aria-hidden="true">
          <span />
        </span>
      </div>
    </section>
  );
};

const styles = () => {
  return {
    root: css`
      display: flex;
      justify-content: space-between;
      align-items: flex-end;
      gap: 16px;
      padding: 16px;
      background-color: #fff;
      border: 1px solid #eee;
    `,
    ticket: css`
      display: grid;
      gap: 8px;
      min-width: 0;
    `,
    ticketName: css`
      margin: 0;
      font-size: 16px;
      font-weight: bold;
      line-height: 1.5;
    `,
    ticketPrice: css`
      margin: 0;
      font-size: 16px;
      font-weight: bold;
      line-height: 1.5;
    `,
    selectWrapper: css`
      position: relative;
      flex: none;
      width: 72px;
      height: 36px;
      background-color: #fff;
      border: 1px solid #ddd;
    `,
    select: css`
      width: 100%;
      height: 100%;
      padding: 0 31px 0 12px;
      color: #222;
      font-size: 14px;
      font-weight: bold;
      line-height: 1.5;
      background-color: transparent;
      border: 0;
      appearance: none;
      cursor: pointer;
      &:focus {
        outline: none;
      }
    `,
    selectIcon: css`
      position: absolute;
      top: 0;
      right: 0;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 30px;
      height: 100%;
      background-color: #f2f2f2;
      pointer-events: none;
      span {
        width: 0;
        height: 0;
        border-top: 6px solid #aaa;
        border-right: 5px solid transparent;
        border-left: 5px solid transparent;
      }
    `,
  };
};
